import styled from 'styled-components';
import { darken } from 'polished';
import {
  Box,
  Button,
  Card as MuiCard,
  Chip as MuiChip,
  TableRow,
  Typography as MuiTypography,
  InputBase,
} from '@mui/material';
import { spacing, SpacingProps } from '@mui/system';
import { StyledProps } from 'types/styled';

export const SearchWrapper = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    display: block;
  }
`;

export const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const StyledSearchBar = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)}px;
    padding-right: ${(props) => props.theme.spacing(2.5)}px;
    padding-bottom: ${(props) => props.theme.spacing(2.5)}px;
    padding-left: ${(props) => props.theme.spacing(2.5)}px;
    width: 400px;
  }
`;

// Use sx prop or styled-components for spacing instead of system spacing utility
export const Card = styled(MuiCard)<SpacingProps>``;

export const Chip = styled(MuiChip)<{ rgbcolor: string }>`
  height: 20px;
  padding: ${(props) => props.theme.spacing(1)} 0; // 4px 0
  font-size: 90%;
  background-color: ${(props) => props.rgbcolor};
  color: ${(props) => props.theme.palette.common.white};
`;

export const ChartWrapper = styled.div`
  height: 280px;
  position: relative;
`;

// Use Box component with sx prop for dynamic spacing instead
export const Spacer = styled.div<SpacingProps>``;

export interface TypographyPropsType extends SpacingProps {
  component?: string;
}

export const Typography = styled(MuiTypography)<TypographyPropsType>(spacing);

export const ClickableRow = styled(TableRow)`
  cursor: pointer;
  white-space: pre;
`;

/** Should provide a max-width for ellipsi */
export const EllipsisDiv = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const WordBreakDiv = styled.div<{
  width?: string;
}>`
  word-break: break-all;
  width: ${({ width }) => width || '20rem'};
`;

export const WhiteBox = styled(Box)<StyledProps>`
  text-align: center;
  border-radius: ${(props) => props.theme.spacing(2.5)}px; // 10px
  white-space: nowrap;
  color: ${(props) => (props.theme.palette.mode === 'dark' ? '#ffffff' : '#000000')};
  border: 1px solid ${(props) => (props.theme.palette.mode === 'dark' ? '#333' : '#d4d4d4')};
  background-color: ${(props) => (props.theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff')};
  padding: ${(props) => props.theme.spacing(1.25, 2)}px; // 5px 8px
`;

export const StyledSimpleCard = styled(MuiCard)`
  padding: 1.5rem;
  height: 100%;
  background-color: ${(props) =>
    props.theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(24px);
  border: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? '1px solid rgba(148, 163, 184, 0.12)'
      : '1px solid rgba(226, 232, 240, 0.8)'};
  border-radius: 16px;
  box-shadow: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset, 0 0 12px rgba(59, 130, 246, 0.06)'
      : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04), 0 0 12px rgba(59, 130, 246, 0.04)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset, 0 0 16px rgba(59, 130, 246, 0.08)'
        : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04), 0 0 16px rgba(59, 130, 246, 0.06)'};
  }

  /* Enhanced heading styles to match table aesthetic */
  h3 {
    color: ${(props) =>
      props.theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.95)' : 'rgba(15, 23, 42, 0.95)'};
    font-weight: 600;
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
    letter-spacing: -0.01em;
  }

  /* Enhanced form field styles */
  .MuiTextField-root,
  .MuiFormControl-root {
    .MuiInputBase-root {
      background-color: ${(props) =>
        props.theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)'};
      border-radius: 8px;

      &:hover {
        background-color: ${(props) =>
          props.theme.palette.mode === 'dark'
            ? 'rgba(30, 41, 59, 0.7)'
            : 'rgba(241, 245, 249, 0.9)'};
      }

      &.Mui-focused {
        background-color: ${(props) =>
          props.theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)'};
      }
    }

    .MuiInputLabel-root {
      color: ${(props) =>
        props.theme.palette.mode === 'dark'
          ? 'rgba(203, 213, 225, 0.7)'
          : 'rgba(71, 85, 105, 0.7)'};

      &.Mui-focused {
        color: ${(props) =>
          props.theme.palette.mode === 'dark'
            ? 'rgba(226, 232, 240, 0.9)'
            : 'rgba(15, 23, 42, 0.9)'};
      }
    }
  }
`;

export const StyledDefaultPrimaryButton = styled(Button).attrs({
  color: 'primary',
})``;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;
`;
